import { IAlarm, SOURCE } from '@alert-manager/lib';

export interface AlarmFilters {
  environment?: string;
  source?: SOURCE;
  status?: 'ongoing' | 'resolved';
  severity?: string;
  project?: string;
  timeFrom?: string;
  timeTo?: string;
  acknowledgedStatus?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface AlarmsResponse {
  alarms: IAlarm[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const user = localStorage.getItem('alertmanager_user');
  if (user) {
    try {
      const parsedUser = JSON.parse(user);
      if (parsedUser.token) {
        headers['Authorization'] = `Bearer ${parsedUser.token}`;
      }
      if (parsedUser.username) {
        headers['X-Username'] = parsedUser.username;
      }
    } catch {
      // Ignore parsing errors
    }
  }

  return headers;
};

export async function getAlarms(filters?: AlarmFilters, pagination?: PaginationParams): Promise<AlarmsResponse> {
  const queryParams = new URLSearchParams();

  // Add filters
  if (filters?.environment) {
    queryParams.append('environment', filters.environment);
  }
  if (filters?.source) {
    queryParams.append('source', filters.source);
  }
  if (filters?.status) {
    queryParams.append('status', filters.status);
  }
  if (filters?.severity) {
    queryParams.append('severity', filters.severity);
  }
  if (filters?.project) {
    queryParams.append('project', filters.project);
  }
  if (filters?.timeFrom) {
    queryParams.append('timeFrom', filters.timeFrom);
  }
  if (filters?.timeTo) {
    queryParams.append('timeTo', filters.timeTo);
  }
  if (filters?.acknowledgedStatus) {
    queryParams.append('acknowledgedStatus', filters.acknowledgedStatus);
  }

  // Add pagination
  if (pagination?.page) {
    queryParams.append('page', pagination.page.toString());
  }
  if (pagination?.limit) {
    queryParams.append('limit', pagination.limit.toString());
  }

  const url = `/api/alarms${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch alarms: ${res.status}`);
  }

  return res.json();
}

// Legacy function for backward compatibility
export async function getAlarmsLegacy(): Promise<IAlarm[]> {
  const response = await getAlarms();
  return response.alarms;
}

export async function createAlarm(alarm: { message: string; severity: string }) {
  const res = await fetch(`/api/alarms`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(alarm),
  });

  if (!res.ok) {
    throw new Error(`Failed to create alarm: ${res.status}`);
  }

  return res.json();
}

export async function acknowledgeAlarm(alarmId: string) {
  const res = await fetch(`/api/alarms/${alarmId}/acknowledge`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });

  if (!res.ok) {
    throw new Error(`Failed to acknowledge alarm: ${res.status}`);
  }

  return res.json();
}

export interface EnvCountsResponse {
  [env: string]: { total: number; critical: number; ongoing: number };
}

export async function getEnvCounts(timeFrom?: string, timeTo?: string): Promise<EnvCountsResponse> {
  const params = new URLSearchParams();
  if (timeFrom) params.append('timeFrom', timeFrom);
  if (timeTo) params.append('timeTo', timeTo);
  const url = `/api/alarms/counts${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch env counts: ${res.status}`);
  return res.json();
}

export async function bulkAcknowledge(ids: string[]) {
  const res = await fetch('/api/alarms/bulk/acknowledge', {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`Failed to bulk acknowledge: ${res.status}`);
  return res.json();
}

export async function bulkResolve(ids: string[], reason?: string) {
  const res = await fetch('/api/alarms/bulk/resolve', {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ids, reason }),
  });
  if (!res.ok) throw new Error(`Failed to bulk resolve: ${res.status}`);
  return res.json();
}

export async function resolveAlarm(alarmId: string, reason?: string) {
  const body = reason ? JSON.stringify({ reason }) : undefined;

  const res = await fetch(`/api/alarms/${alarmId}/resolve`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: body,
  });

  if (!res.ok) {
    throw new Error(`Failed to resolve alarm: ${res.status}`);
  }

  return res.json();
}
